import React, {useCallback, useEffect, useState} from "react";
import {DateObject} from "react-multi-date-picker";
import {useDispatch} from "react-redux";
import Avatar from "../../../system/components/avatar";
import {useTranslate} from "../../../system/useTranslate";
import {setMessage} from "../../../system/app-slice";
import {upsertWorkspaceTask} from "../../../system/workspace-graph-slice";
import {useTask} from "../../../models/task/use-task";
import TaskSeries from "../../../models/task-series/task-series";
import {useRecurrenceScope} from "./recurrence-scope-dialog";
import {RecurrenceBadge} from "./task-recurrence-badge";

const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_PROJECTION_DAYS = 92;

/** FullCalendar event for a persisted task (same shape the three calendars used before). */
export function calendarEventFromTask(task) {
    const done = task.taskStatus === "done";
    return {
        id: task.documentId,
        title: task.title,
        backgroundColor: done ? "var(--surface-50)" : "var(--green-500)",
        borderColor: done ? "var(--surface-200)" : "var(--green-500)",
        textColor: done ? "black" : "white",
        start: new DateObject(task.startAt).format("YYYY-MM-DD"),
        end: new DateObject(task.dueDate).format("YYYY-MM-DD"),
        data: task,
    };
}

/** FullCalendar event for a computed (not yet saved) occurrence returned by /task-series/projections. */
export function projectedCalendarEvent(projection) {
    const due = projection.dueDate || projection.scheduledDueAt;
    const start = projection.startAt || due;
    return {
        id: `projected:${projection.seriesId}#${projection.sequenceNumber}`,
        title: projection.title,
        backgroundColor: "var(--surface-50)",
        borderColor: "var(--green-500)",
        textColor: "black",
        start: new DateObject(start).format("YYYY-MM-DD"),
        end: new DateObject(due).format("YYYY-MM-DD"),
        data: {
            ...projection,
            projected: true,
            series: projection.recurrence,
            taskStatus: "todo",
            scheduledDueAt: projection.scheduledDueAt,
        },
    };
}

/** Event body shared by the task, project and team calendars. */
export function CalendarEventContent(arg) {
    const taskData = arg.event.extendedProps.data;
    const done = taskData?.taskStatus === "done";
    const skipped = taskData?.occurrenceStatus === "skipped";
    return (
        <div data-task-sidebar-trigger="true" className={`flex align-items-center gap-2 p-1 ${(done || skipped) ? "line-through opacity-60 text-400" : ""}`}>
            {taskData?.assignee?.member && <Avatar className="text-3xs text-600" style={{width: "1.3rem", height: "1.3rem"}} memberData={taskData.assignee.member}/>}
            <div className="flex flex-column text-xs">
                <span className="text-xs">{arg.event.title}</span>
                <span className="opacity-70 flex align-items-center gap-1">
                    {arg.timeText}
                    <RecurrenceBadge task={taskData}/>
                </span>
            </div>
        </div>
    );
}

function durationToMs(duration) {
    if (!duration) return 0;
    return (duration.days || 0) * DAY_MS + (duration.milliseconds || 0);
}

/**
 * Recurring tasks in a calendar:
 * - loads the computed occurrences beyond the saved window for the visible range (`projectedEvents`);
 * - clicking one saves it first, then opens it;
 * - dragging/resizing a repeating task asks which occurrences the change applies to (this / following / all).
 * Persisted non-recurring tasks keep their previous behaviour.
 */
export function useRecurrenceCalendar({tasks, updateTask, setSelectedTask, projectDocumentId, assigneeDocumentId, enabled = true}) {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const taskModel = useTask();
    const {askScope, scopeDialog} = useRecurrenceScope();
    const [range, setRange] = useState(null);
    const [projectedEvents, setProjectedEvents] = useState([]);

    const rangeStart = range?.start?.getTime();
    const rangeEnd = range?.end?.getTime();
    useEffect(() => {
        if (!enabled || !rangeStart || !rangeEnd) return undefined;
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const to = Math.min(rangeEnd, rangeStart + MAX_PROJECTION_DAYS * DAY_MS);
                const res = await new TaskSeries().projections({
                    from: new Date(rangeStart).toISOString(),
                    to: new Date(to).toISOString(),
                    ...(projectDocumentId ? {projects: projectDocumentId} : {}),
                    ...(assigneeDocumentId ? {assignee: assigneeDocumentId} : {}),
                });
                if (!cancelled) setProjectedEvents((res?.data || []).map(projectedCalendarEvent));
            } catch (error) {
                if (!cancelled) setProjectedEvents([]);
            }
        }, 400);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [enabled, rangeStart, rangeEnd, projectDocumentId, assigneeDocumentId, tasks]);

    const onRangeChange = useCallback((start, end) => setRange({start, end}), []);

    const notify = (content) => dispatch(setMessage({severity: "error", content}));

    // Saves a computed occurrence so it can be opened or edited.
    const materialize = async (projection) => {
        try {
            const res = await new TaskSeries().materialize(projection.seriesId, projection.sequenceNumber);
            const full = await taskModel.getTask(res?.data?.documentId);
            if (full) {
                dispatch(upsertWorkspaceTask(full));
                return full;
            }
        } catch (error) {
            const code = error?.details?.code || error?.code || error?.message;
            notify(typeof code === "string" && code.startsWith("recurrence.") ? translate(code) : translate("task-delay.submit-error"));
        }
        return null;
    };

    const onEventClick = async (info) => {
        const data = info.event.extendedProps.data;
        if (!data?.projected) {
            setSelectedTask(data);
            return;
        }
        const saved = await materialize(data);
        if (saved) setSelectedTask(saved);
    };

    const applyMove = async (info, {startShift, dueShift}) => {
        let task = info.event.extendedProps.data;
        if (task?.projected) {
            task = await materialize(task);
            if (!task) {
                info.revert();
                return;
            }
        }
        if (!task.series) {
            await updateTask(task, {startAt: info.event.start, dueDate: info.event.end});
            return;
        }
        const scope = await askScope("edit");
        if (!scope) {
            info.revert();
            return;
        }
        // Keep the time of day: shift the saved date-times by the dragged amount.
        const shifted = (value, shift) => (value && shift ? new Date(new Date(value).getTime() + shift) : undefined);
        const data = {recurrenceScope: scope};
        const nextStart = shifted(task.startAt, startShift);
        const nextDue = shifted(task.dueDate, dueShift);
        if (nextStart) data.startAt = nextStart;
        if (nextDue) data.dueDate = nextDue;
        await updateTask(task, data);
    };

    const onEventDropped = (info) => {
        const shift = durationToMs(info.delta);
        return applyMove(info, {startShift: shift, dueShift: shift});
    };

    const onEventResize = (info) => applyMove(info, {
        startShift: durationToMs(info.startDelta),
        dueShift: durationToMs(info.endDelta),
    });

    return {projectedEvents, onRangeChange, onEventClick, onEventDropped, onEventResize, scopeDialog};
}
